import React, {
  forwardRef,
  ReactElement,
  InputHTMLAttributes,
  useRef,
} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './date.css';
import FormField from '../../common/form-field';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { parseError } from '../../../services/utils';
import readInputCss from './read-input.module.css';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDay } from '@fortawesome/pro-solid-svg-icons';

export default function Date(
  name: string,
  label: string = null,
  min?: Date,
  max?: Date,
  horizontal?: boolean,
  icon?: string | ReactElement,
  column?: boolean,
  requiredShow?: boolean,
  placeholderText?: string
): FormComponent {
  return ({ ...rest }) => (
    <DateComponent
      name={name}
      label={label}
      min={min}
      max={max}
      horizontal={horizontal}
      icon={icon}
      column={column}
      requiredShow={requiredShow}
      placeholderText={placeholderText}
      {...rest}
    />
  );
}

interface CustomInputProps extends InputHTMLAttributes<HTMLInputElement> {
  icon?: string | ReactElement;
}

//Custom input
const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (props: any, ref) => {
    return (
      <label>
        <input {...props} ref={ref} />
        {props.icon ? (
          typeof props.icon === 'object' ? (
            props.icon
          ) : (
            <img
              className="svg-inline--fa"
              src={props.icon}
              alt=""
              width={20}
              height={20}
            />
          )
        ) : (
          <FontAwesomeIcon icon={faCalendarDay} />
        )}
      </label>
    );
  }
);

export function DateComponent({
  name,
  label,
  parentName,
  disabled,
  readonly,
  required,
  min,
  max,
  horizontal = false,
  icon,
  column,
  placeholderText,
  requiredShow = false,
}: FormComponentProps & {
  name: string;
  label: string;
  required?: string[];
  min?: Date;
  max?: Date;
  horizontal?: boolean;
  icon?: string | ReactElement;
  column?: boolean;
  requiredShow?: boolean;
  placeholderText: string;
}) {
  const { t } = useTranslation();
  const {
    formState: { errors },
  } = useFormContext();
  const dateName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(dateName, errors);

  const {
    field: { value: defaultValue, onBlur, onChange },
  } = useController({ name: dateName, shouldUnregister: false });
  const refCustomInput = useRef();
  const className = ''
    .concat('react-datepicker-container', ' ')
    .concat(column ? 'column' : '', ' ');
  const fallbackLabel = column ? null : ' ';
  const formFieldLabel = label
    ? t(label)
    : (label === null && t(name) === name)
    ? fallbackLabel
    : t(name);

  const value = defaultValue ? defaultValue : undefined;

  if (readonly) {
    return (
      <FormField
        labelFor={dateName}
        column={column}
        label={formFieldLabel}
        error={error ? error.message : null}
        horizontal={horizontal}
      >
        <input
          type="text"
          name={dateName}
          className={readInputCss['readonly']}
          readOnly={true}
          value={value ? moment(value).format('DD.MM.YYYY') : ''}
        />
      </FormField>
    );
  } else {
    return (
      <FormField
        labelFor={dateName}
        column={column}
        label={formFieldLabel}
        required={required?.includes(dateName) || requiredShow}
        error={error ? error.message : null}
        horizontal={horizontal}
      >
        <div className={className}>
          <DatePicker
            name={dateName}
            disabled={disabled}
            locale="lv-LV"
            dateFormat="dd.MM.yyyy"
            selected={value && new window.Date(value.toString())}
            placeholderText={placeholderText}
            onChange={(date, event) => {
              const dateObject = date ? new window.Date(date.toString()) : null;

              onChange(
                dateObject
                  ? new window.Date(
                      window.Date.UTC(
                        dateObject.getFullYear(),
                        dateObject.getMonth(),
                        dateObject.getDate()
                      )
                    )
                  : null
              );
            }}
            onBlur={onBlur}
            customInput={<CustomInput ref={refCustomInput} icon={icon} />}
            autoComplete={'off'}
            minDate={min}
            maxDate={max}
          />
        </div>
      </FormField>
    );
  }
}
